<template>
  <div
    v-if="$store.getters.showSiteLoader"
    id="app"
    class="full-page-loader"
  >
    <Loader />
  </div>

  <div
    v-else
    id="app"
  >
    <Header />

    <b-alert
      ref="successAlert"
      :show="successDismissCountDown"
      variant="success"
      dismissible
      fade
      @dismissed="successDismissCountDown=0"
    >
      {{ $store.getters.globalSuccessAlertText }}
    </b-alert>

    <b-alert
      ref="errorAlert"
      :show="errorDismissCountDown"
      variant="danger"
      dismissible
      fade
      @dismissed="errorDismissCountDown=0"
    >
      {{ $store.getters.globalErrorAlertText }}
    </b-alert>

    <mfa-approaching-modal ref="mfaModal" />
    <inactive-timeout-modal />

    <main>
      <router-view />
    </main>
    <Footer />
  </div>
</template>

<script>
/* eslint-disable func-names */

import StorageService from '@/api/Storage';
import UserClient from '@/api/UserClient';
import Footer from '@/components/Footer.vue';
import Header from '@/components/Header.vue';
import Loader from '@/components/Loader.vue';
import InactiveTimeoutModal from '@/components/modals/InactiveTimeoutModal.vue';
import MfaApproachingModal from '@/components/modals/MfaApproachingModal.vue';

const ALERT_TIMEOUT = 10;

export default {
  name: 'App',
  components: {
    Loader,
    Header,
    Footer,
    MfaApproachingModal,
    InactiveTimeoutModal,
  },
  data() {
    return {
      userClient: new UserClient(this.$http),

      successDismissCountDown: 0,
      errorDismissCountDown: 0,
    };
  },
  watch: {
    // watch the global alert triggers to show the alert here
    // eslint-disable-next-line no-unused-vars
    '$store.getters.showGlobalSuccessAlert': function (val, oldVal) {
      if (val) {
        this.successDismissCountDown = ALERT_TIMEOUT;

        // hack to add a "open" class when alert appears to have it animate down nicely
        setTimeout(() => {
          this.$refs.successAlert.$el.classList.add('open');
        }, 300);
      } else {
        this.successDismissCountDown = 0;
      }
    },
    // eslint-disable-next-line no-unused-vars
    '$store.getters.showGlobalErrorAlert': function (val, oldVal) {
      if (val) {
        this.errorDismissCountDown = ALERT_TIMEOUT;

        // hack to add a "open" class when alert appears to have it animate down nicely
        setTimeout(() => {
          this.$refs.errorAlert.$el.classList.add('open');
        }, 300);
      } else {
        this.errorDismissCountDown = 0;
      }
    },

    // eslint-disable-next-line no-unused-vars
    '$store.getters.mfaWarning': function (val, oldVal) {
      if (val && val.show) {
        this.$refs.mfaModal.show();
      }
    },
    // when we close the alert here, update the global
    successDismissCountDown(val) {
      if (val <= 0) {
        this.$store.commit('clearGlobalAlert', false);
      }
    },
    errorDismissCountDown(val) {
      if (val <= 0) {
        this.$store.commit('clearGlobalAlert', true);
      }
    },
  },
  created() {
    // eslint-disable-next-line no-underscore-dangle
    const _failedToAuth = () => {
      this.$store.dispatch('clearUser');
      const currentPath = window.location.pathname;
      // eslint-disable-next-line global-require
      const pathConstants = require('@/constants/routes').default;

      // check if current route allows anonymous view

      // TODO: Come up with a better check for routes with params
      const isActivating = currentPath.indexOf('/confirm/') !== -1 || currentPath.indexOf('/reset/') !== -1;

      if (!isActivating
        && pathConstants.filter(p => p.path === currentPath && p.allowAnonymous).length === 0) {
        return this.$router.push({ name: 'login' });
      }
      return null;
    };

    if (StorageService.getItem('accessToken')) {
      this.userClient.getRemoteUser().then((user) => {
        if (user) {
          this.$store.commit('setUser', user);
          this.userClient.getUserMfaMethods().then((mfaResponse) => {
            this.$store.commit('setUserMfa', mfaResponse);
            if (this.$store.getters.mfaIsRequired) {
              this.$router.replace('/account');
            }
          });
          this.userClient.shouldShowTestFixture().then((showTestFixture) => {
            this.$store.commit('setShowTestFixture', showTestFixture);
          });
        } else {
          _failedToAuth();
        }
        this.$store.dispatch('loadedRemoteUser');
      }).catch(() => {
        this.$store.dispatch('loadedRemoteUser');
        _failedToAuth();
      });
    } else {
      this.$store.dispatch('loadedRemoteUser');
      _failedToAuth();
    }
  },
};
</script>

<style lang="scss">
@import "/scss/styles.scss";

main {
  min-height: calc(100vh - (calc(#{$header-height} + 15px)));
  padding-top: calc(#{$header-height} + #{$gutter});
  background-color: $bg-color;
}

main:has(#redoc-container) {
  padding-top: $header-height !important;
}

.header-row {
  margin-bottom: 15px;

  h1 {
    font-size: 25px;

    @media (max-width: $sm-max) {
      margin-bottom: 20px;
    }
  }

  .custom-select {
    display: none;
    width: calc(100% - 200px);
    height: 42px;
    padding: 0 15px;
    vertical-align: top;
    cursor: pointer;

    &.full-width {
      width: 100%;
    }

    @media (min-width: $md-min) {
      position: relative;
      top: -5px;
      display: inline-block;
    }
  }

  .align-fix {
    div {
      @media (min-width: $md-min) {
        display: inline-block;
        float: right;
        vertical-align: top;
      }
    }
  }

  @media (max-width: $sm-max) {
    button {
      width: calc(100%);
      height: 50px;
      margin-bottom: env(safe-area-inset-bottom);
    }

    .form {
      width: 100%;
    }
  }

  @media (min-width: $md-min) {
    button {
      position: relative;
      top: -5px;
      float: right;
    }

    .org-header-bar .form {
      width: calc(100% - 350px);
    }

    .form {
      top: -13px;
      width: calc(100% - 150px);

      .form__input input.input {
        margin-bottom: 0;
      }
    }
  }

  .form {
    position: relative;
    display: inline-block;
  }
}

.no-results {
  @include border-radius($border-radius);

  height: 300px;
  margin-bottom: $gutter;
  background: white;
  border: solid 1px #ddd;
  border-top: solid 3px $red;
}
</style>
